import React, { useEffect, useState } from "react";
import { X, FileText, Loader2, ExternalLink, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { getJSON } from "../../lib/api";

const SOURCE_TONE = {
    from_client: "text-accent-cyan border-accent-cyan/30",
    blank: "text-text-muted border-white/[0.08]",
};

const VENDOR_LABEL = {
    fortinet: "Fortinet", watchguard: "WatchGuard",
    zyxel: "Zyxel", pfsense: "pfSense",
};

const fmtDate = (s) => {
    if (!s) return "";
    try {
        const d = new Date(s);
        return d.toLocaleString(undefined, {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return s; }
};

/** Phase 7c.4 — modal opened from the client Profile tab. Lists ZeroConf
 *  configs that were saved while linked to this client. Click an entry to
 *  resume in the wizard (deep-link via ?config={id} — the wizard already
 *  knows how to hydrate from a config id on mount). */
export const RecentConfigsModal = ({ client, onClose }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getJSON(`/ipam/clients/${client.id}/saved-configs`)
            .then((d) => { if (!cancelled) setItems(d.items || []); })
            .catch((e) => { if (!cancelled) setErr(e.message || "Failed to load"); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [client.id]);

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-canvas/85 backdrop-blur-md"
            role="dialog"
            aria-modal="true"
            aria-label="Recent configs"
            data-testid="recent-configs-modal"
        >
            <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 cursor-default" />
            <div className="relative w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col rounded-2xl border border-white/[0.08] bg-surface/95 shadow-heavy-popover">
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
                    <div>
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                            Saved configurations
                        </p>
                        <h2 className="mt-1 font-display font-semibold text-xl text-text-primary tracking-tight">
                            {client.name}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="text-text-dim hover:text-text-primary p-1.5 rounded"
                        data-testid="recent-configs-close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="overflow-y-auto p-3">
                    {loading ? (
                        <div className="py-10 text-center text-text-muted flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                        </div>
                    ) : err ? (
                        <div className="px-4 py-6 text-[12.5px] text-accent-red">{err}</div>
                    ) : items.length === 0 ? (
                        <div className="py-12 text-center border border-dashed border-white/[0.06] rounded-lg mx-2" data-testid="recent-configs-empty">
                            <FileText className="w-5 h-5 text-text-dim mx-auto" />
                            <p className="mt-3 text-[13px] text-text-muted">No configs saved yet for this client.</p>
                            <p className="mt-1 text-[11.5px] text-text-dim">
                                Run the wizard from this client to autopopulate subnets, firewall and DNS.
                            </p>
                            <Link
                                to="/portal"
                                className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-cyan text-canvas text-[12px] font-medium hover:shadow-glow-cyan"
                            >
                                Open ZeroConf wizard <ExternalLink className="w-3 h-3" />
                            </Link>
                        </div>
                    ) : (
                        <ul className="divide-y divide-white/[0.05]" data-testid="recent-configs-list">
                            {items.map((c) => (
                                <li key={c.id} className="px-3 py-3 hover:bg-elevated/40 transition-colors" data-testid={`recent-config-${c.id}`}>
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="text-[13.5px] text-text-primary truncate">{c.name || "Untitled draft"}</div>
                                            <div className="mt-0.5 flex items-center gap-2 text-[11.5px] text-text-dim">
                                                <span>{VENDOR_LABEL[c.vendor] || c.vendor || "—"}</span>
                                                {c.model && <><span>·</span><span className="font-mono">{c.model}</span></>}
                                                <span>·</span>
                                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono uppercase ${SOURCE_TONE[c.source || "blank"]}`}>
                                                    {c.source === "from_client" ? "from client" : "standalone"}
                                                </span>
                                            </div>
                                            <div className="mt-1 flex items-center gap-1 text-[11px] text-text-dim">
                                                <Clock className="w-3 h-3" /> {fmtDate(c.updated_at || c.created_at)}
                                            </div>
                                        </div>
                                        <Link
                                            to={`/portal?config=${c.id}`}
                                            onClick={onClose}
                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-white/[0.08] text-text-muted hover:text-accent-cyan hover:border-accent-cyan/30 text-[11.5px]"
                                            data-testid={`recent-config-open-${c.id}`}
                                        >
                                            Open <ExternalLink className="w-3 h-3" />
                                        </Link>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RecentConfigsModal;
